import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import Conversation from "./Conversation";
import ChatInput from "./ChatInput";
import Icon from "../../YourAIAgents/components/components/icon";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import TaskCard from "./WelcomeSections/TaskCard";
import TasksFilterBar, { NO_TOOL_VALUE } from "./WelcomeSections/TasksFilterBar";
import useTaskCardActions from "../hooks/useTaskCardActions";
import suggestAutomation from "../utils/suggestAutomation";
import { formatCadence, isScheduled } from "../utils/taskSchedule";
import { PROVEN_TASK_TYPES } from "../utils/provenTasks";
import { useAgencyPromptData, useTeamPromptData } from "../../../queries/tasks";
import { useAvailableIntegrations } from "../../../queries/integrations";
import { useAgencyAxisOptions } from "../../../queries/agencyAxisOptions";
import { useToolCatalog, getTaskToolNames, normaliseToolKey } from "../hooks/useToolCatalog";
import {
  AXES,
  AXIS_QUERY_KEYS,
  getAxisValues,
  findAxisOption,
  getAxisOptionValue,
  getAxisOptionLabel,
  setAxisLabels,
} from "../../../lib/onboardingAxes";
import { resolveTaskById } from "../../../lib/taskLookup";
import SaveShortcutPopup from "./WelcomeSections/SaveShortcutPopup";
import { useShortcuts } from "../../../hook/useShortcuts";
import { requestShortcutHighlight } from "../../../lib/shortcuts";


const ConversationWrapper = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const { mutateAsync: fetchAgencyPromptData } = useAgencyPromptData();

  // The Task Library shows a compact preview by default; "Your Tasks" (nav) and
  // the "Explore more" card expand it to the full grid via the #task-library
  // hash. This is what replaced the standalone All-Tasks page.
  const libraryExpanded = location.hash === "#task-library";
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  // URL axis values that matched no configured option (e.g. ?job_role=CEO
  // when the agency's role is actually "Founder/CEO") — shown as a distinct
  // "not found" chip so a bad link is visible instead of silently dropped.
  const [unmatchedAxisValues, setUnmatchedAxisValues] = useState([]);
  const [scope, setScope] = useState("you");
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]); // teammate ids, excludes self
  const [prefillMessage, setPrefillMessage] = useState("");
  const [provenTasks, setProvenTasks] = useState([]);
  const [yourTasksLoaded, setYourTasksLoaded] = useState(false);
  const [provenTasksLoaded, setProvenTasksLoaded] = useState(false);
  const [teamTasksData, setTeamTasksData] = useState(null);
  const [teamTasksLoaded, setTeamTasksLoaded] = useState(false);
  const teamTasksLoadingRef = useRef(false);
  // 'hidden' | 'prompt' | 'saved' — "saved" is a confirmation the pop-up
  // morphs into after "Save shortcut" is clicked (matches the reference
  // prototype), not a separate toast, and it stays up until dismissed.
  const [popupPhase, setPopupPhase] = useState("hidden");
  const [savedShortcutId, setSavedShortcutId] = useState(null);
  const { findByFilters, save: saveShortcut, rename: renameShortcut, remove: removeShortcut } = useShortcuts();

  const {
    selectedAssistant,
    handleSendMessage,
    setMessages,
    createConvesation,
    tasksData,
    fetchTasksData,
    resetAttachedFileSize,
    setIsUserChating,
    isUserChating,
    markMessagePending,
    contextuploadFilefrominput,
    locationUsers,
    requestLocationUsers,
    agency,
  } = useUsersContext();
  const { mutateAsync: fetchTeamPromptData } = useTeamPromptData();
  const { data: availableIntegrationsData } = useAvailableIntegrations(
    userActiveLocation?.id,
    userInfo?.userId,
    userActiveLocation?.agency_id,
  );
  const {
    categories: agencyCategories,
    roles: agencyRoles,
    sectors: agencySectors,
  } = useAgencyAxisOptions();

  useEffect(() => {
    if (selectedFiles.length === 0) resetAttachedFileSize?.();
  }, [selectedFiles]);

  const { bindActions, actionModals } = useTaskCardActions();

  useEffect(() => {
    fetchTasksData?.()?.finally(() => setYourTasksLoaded(true));
  }, []);

  // The Team tab needs the roster to list teammates to toggle — fetched
  // eagerly (same lazy-but-unconditional pattern as ShareModal) rather than
  // gated behind scope==="team", since reaching "team" scope in the first
  // place usually means picking a teammate from this exact list.
  useEffect(() => {
    requestLocationUsers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const agencyId = agency?._id;
    if (!agencyId) return;
    fetchAgencyPromptData({ agencyId })
      .then((response) => {
        setProvenTasks(response?.data?.prompt || []);
      })
      .catch((err) => {
        console.error("[ProvenTasks] request failed", err);
      })
      .finally(() => setProvenTasksLoaded(true));
  }, [agency, fetchAgencyPromptData]);


  useEffect(() => {
    if (scope !== "team" && scope !== "scheduled") return;
    if (teamTasksLoaded || teamTasksLoadingRef.current) return;
    teamTasksLoadingRef.current = true;
    fetchTeamPromptData({
      userId: userInfo?._id,
      locationId: userActiveLocation?.id,
      agencyId: userActiveLocation?.agency_id,
    })
      .then((response) => {
        setTeamTasksData(response?.data?.prompt || []);
      })
      .catch((err) => {
        console.error("[TeamTasks] request failed", err);
      })
      .finally(() => {
        teamTasksLoadingRef.current = false;
        setTeamTasksLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, teamTasksLoaded]);


  useEffect(() => {
    if (!libraryExpanded) return;
    const id = setTimeout(() => {
      document
        .getElementById("task-library")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(id);
  }, [libraryExpanded]);

  // "Save this view" pop-up: fires on any fresh navigation entry that lands
  // with an onboarding axis param in the URL — both a resolved short-link
  // redirect (/xero, /tool/xero) AND a direct combined link straight to
  // /app/chat (e.g. ?utm_source=partner&utm_campaign=onboarding&tool=Xero)
  // — and never twice for the same entry. A sidebar Shortcut click also
  // lands here (plain navigate, same URL shape) but is excluded at render
  // time via `shortcutMatch`, not here, since by the time this timeout fires
  // the URL->filter sync effect may have only just settled the real filter
  // state: a shortcut's own URL always matches itself, so the popup never
  // shows for one.
  useEffect(() => {
    setPopupPhase("hidden");
    const params = new URLSearchParams(location.search);
    const hasAxisParam = AXES.some((axis) => params.has(AXIS_QUERY_KEYS[axis]));
    if (!hasAxisParam) return;
    const id = setTimeout(() => setPopupPhase("prompt"), 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // An explicit ?task= or ?prompt= always pre-fills the box and takes
  // precedence over a Tool link's ghost placeholder (below).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const explicit = params.get("prompt") || params.get("task");
    if (explicit) setPrefillMessage(explicit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handlePrefillConsumed = useCallback(() => setPrefillMessage(""), []);

  // Arriving via a Tool link/shortcut (?tool=...) with nothing explicit to
  // pre-fill shows ghost placeholder text instead of pre-filling the box.
  // Category/Sector/Role links are unaffected (only the "tool" axis key).
  const toolGhostLabel = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("prompt") || params.get("task")) return null;
    const labels = getAxisValues(params, "tool");
    return labels.length ? labels.join(" or ") : null;
  }, [location.search]);

  // ?taskId=<live _id> opens that exact Task's instructions in the composer
  // (same "selection", not "run", mechanism as clicking a TaskCard) — never
  // submits/runs it or stamps run history. An explicit ?task=/?prompt= still
  // wins (handled above). Waits for both task pools to load before deciding
  // a given id is genuinely unavailable, rather than flashing that state.
  const taskIdParam = new URLSearchParams(location.search).get("taskId");
  const taskIdLookup = useMemo(
    () =>
      resolveTaskById(
        taskIdParam,
        [tasksData?.prompt || [], provenTasks],
        yourTasksLoaded && provenTasksLoaded,
      ),
    [taskIdParam, tasksData, provenTasks, yourTasksLoaded, provenTasksLoaded],
  );
  const taskIdMatch = taskIdLookup.status === "found" ? taskIdLookup.task : null;
  const taskIdUnavailable = taskIdLookup.status === "unavailable";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("prompt") || params.get("task")) return; // explicit prompt wins
    if (taskIdMatch?.prompt_value) setPrefillMessage(taskIdMatch.prompt_value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdMatch]);

  const toggleTool = useCallback((tool) => {
    setSelectedTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  }, []);

  const clearTools = useCallback(() => setSelectedTools([]), []);

  const toggleCategory = useCallback((id) => {
    setSelectedCategories((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);
  const toggleRole = useCallback((id) => {
    setSelectedRoles((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);
  const toggleSector = useCallback((id) => {
    setSelectedSectors((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const connectedToolKeys = useMemo(() => {
    const list = availableIntegrationsData?.connected || [];
    return new Set(
      list
        .map((item) => normaliseToolKey(item?.provider_name || item?.provider || item?.name))
        .filter(Boolean),
    );
  }, [availableIntegrationsData]);

  const excludeCurated = (list) =>
    (list || []).filter((t) => {
      const type = String(t?.type);
      return type !== "SuperAdmin" && type !== "super-admin";
    });

  const yourPool = useMemo(() => excludeCurated(tasksData?.prompt), [tasksData]);
  const wholeTeamPool = useMemo(() => excludeCurated(teamTasksData), [teamTasksData]);
  const scheduledPool = useMemo(() => wholeTeamPool.filter(isScheduled), [wholeTeamPool]);
  const provenPool = useMemo(
    () => provenTasks.filter((t) => PROVEN_TASK_TYPES.includes(String(t?.type))),
    [provenTasks],
  );
  // Narrows "Whole team" to specific teammates picked in the Team tab; empty
  // selection means the unfiltered whole-team pool.
  const teamFilteredPool = useMemo(() => {
    if (selectedTeamMembers.length === 0) return wholeTeamPool;
    return wholeTeamPool.filter((t) => {
      const owners = Array.isArray(t?.userId) ? t.userId : [];
      return owners.some((owner) => selectedTeamMembers.includes(String(owner?.id)));
    });
  }, [wholeTeamPool, selectedTeamMembers]);

  // Tool counts/no-tool count are scoped to the active Show scope (before
  // tool filtering is applied), same data the task grid itself draws from.
  const scopeBaseList = useMemo(() => {
    if (scope === "team") return teamFilteredPool;
    if (scope === "scheduled") return scheduledPool;
    if (scope === "proven") return provenPool;
    return yourPool;
  }, [scope, teamFilteredPool, scheduledPool, provenPool, yourPool]);

  const { toolMatchCounts, noToolCount } = useMemo(() => {
    const counts = new Map();
    let none = 0;
    scopeBaseList.forEach((t) => {
      const names = getTaskToolNames(t);
      if (names.length === 0) {
        none += 1;
        return;
      }
      names.forEach((name) => {
        const key = normaliseToolKey(name);
        if (key) counts.set(key, (counts.get(key) || 0) + 1);
      });
    });
    return { toolMatchCounts: counts, noToolCount: none };
  }, [scopeBaseList]);

  // Category is a single value per task (its id, per Agency.Categories
  // convention); Role/Sector are arrays of ids — mirrors prompts.js's schema.
  const axisMatchCounts = useMemo(() => {
    const categoryCounts = new Map();
    const roleCounts = new Map();
    const sectorCounts = new Map();
    scopeBaseList.forEach((t) => {
      if (t?.category) categoryCounts.set(t.category, (categoryCounts.get(t.category) || 0) + 1);
      (t?.job_roles || []).forEach((id) => roleCounts.set(id, (roleCounts.get(id) || 0) + 1));
      (t?.sectors || []).forEach((id) => sectorCounts.set(id, (sectorCounts.get(id) || 0) + 1));
    });
    return { categoryCounts, roleCounts, sectorCounts };
  }, [scopeBaseList]);

  const categoryOptions = useMemo(
    () =>
      agencyCategories.map((c) => ({
        value: c.id,
        label: c.name,
        count: axisMatchCounts.categoryCounts.get(c.id) || 0,
      })),
    [agencyCategories, axisMatchCounts],
  );
  const roleOptions = useMemo(
    () =>
      agencyRoles.map((r) => ({
        value: r.id,
        label: r.name,
        count: axisMatchCounts.roleCounts.get(r.id) || 0,
      })),
    [agencyRoles, axisMatchCounts],
  );
  const sectorOptions = useMemo(
    () =>
      agencySectors.map((s) => ({
        value: s.id,
        label: s.name,
        count: axisMatchCounts.sectorCounts.get(s.id) || 0,
      })),
    [agencySectors, axisMatchCounts],
  );

  // The Tools tab should list every tool this location can connect, not only
  // the ones already attached to a task — so it's seeded from both the
  // actual task integrations AND the full /integrations/available catalog,
  // deduped by a loosely-normalised key since the two sources spell providers
  // differently ("Google Docs" task integration vs "google-docs" Nango
  // provider key). Base catalog is shared with the onboarding short-link
  // resolver (useToolCatalog); this annotates it with connected/count.
  const toolCatalog = useToolCatalog(tasksData?.prompt, availableIntegrationsData?.integrations);
  const toolOptions = useMemo(
    () =>
      toolCatalog.map((opt) => {
        const key = normaliseToolKey(opt.value);
        return {
          ...opt,
          connected: connectedToolKeys.has(key),
          count: toolMatchCounts.get(key) || 0,
        };
      }),
    [toolCatalog, connectedToolKeys, toolMatchCounts],
  );

  // Onboarding short links (and sidebar Shortcuts) are fully URL-driven for
  // these 4 axes: whenever any of tool/category/role/sector appears in the
  // query string, it's treated as the authoritative filter state — replacing
  // all 4 axes (an absent key means that axis is cleared) — so clicking a
  // Shortcut while already on this page swaps the filter immediately with no
  // reload, and arriving fresh pre-ticks the matching dropdown entries.
  // Scope/search/team aren't part of this and are left untouched.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasAnyAxis = AXES.some((axis) => params.has(AXIS_QUERY_KEYS[axis]));
    if (!hasAnyAxis) return;

    // A label that matches nothing fails silently by design (no fuzzy
    // matching — see onboardingAxes.js) — which, from the URL, looks
    // identical to "the agency just hasn't configured that value" and is
    // otherwise invisible. Warning here turns a silent no-op into a
    // diagnosable message instead of a support back-and-forth, and the
    // unmatched labels themselves surface as a distinct "not found" chip
    // (unmatchedAxisValues below) so it's visible without opening devtools.
    const allUnmatched = [];
    const resolve = (axis, list) => {
      const labels = getAxisValues(params, axis);
      const matched = labels.map((label) => findAxisOption(axis, list, label));
      const unmatched = labels.filter((_, i) => !matched[i]);
      if (unmatched.length) {
        console.warn(
          `[onboarding-axis] "${unmatched.join(", ")}" didn't match any configured ${axis} — configured options: ${list.map((o) => getAxisOptionLabel(axis, o)).join(", ") || "(none)"}`,
        );
        unmatched.forEach((label) => allUnmatched.push({ axis, label }));
      }
      return matched.filter(Boolean).map((opt) => getAxisOptionValue(axis, opt));
    };

    setSelectedTools(resolve("tool", toolCatalog));
    setSelectedCategories(resolve("category", agencyCategories));
    setSelectedRoles(resolve("role", agencyRoles));
    setSelectedSectors(resolve("sector", agencySectors));
    setUnmatchedAxisValues(allUnmatched);
  }, [location.search, toolCatalog, agencyCategories, agencyRoles, agencySectors]);

  // The query string a Shortcut for the CURRENT filter selections would use
  // — display labels only (same convention the short-link redirect itself
  // produces), and only the 4 onboarding axes (not prompt/task/campaign
  // params), since that's what a shortcut is meant to restore. Also folds in
  // each axis's still-unmatched raw values (unmatchedAxisValues) so saving
  // never silently drops part of the URL the user actually arrived with —
  // the shortcut reopens to the exact same state, "not found" chip included,
  // instead of only the subset that happened to resolve.
  const buildFilterSearchString = useCallback(() => {
    const params = new URLSearchParams();
    const addAxis = (axis, values, options) => {
      const resolvedLabels = values
        .filter((v) => v !== NO_TOOL_VALUE)
        .map((v) => options.find((opt) => opt.value === v)?.label)
        .filter(Boolean);
      const unmatchedLabels = unmatchedAxisValues
        .filter((u) => u.axis === axis)
        .map((u) => u.label);
      setAxisLabels(params, axis, [...resolvedLabels, ...unmatchedLabels]);
    };
    addAxis("tool", selectedTools, toolOptions);
    addAxis("category", selectedCategories, categoryOptions);
    addAxis("role", selectedRoles, roleOptions);
    addAxis("sector", selectedSectors, sectorOptions);
    return `?${params.toString()}`;
  }, [
    selectedTools,
    selectedCategories,
    selectedRoles,
    selectedSectors,
    toolOptions,
    categoryOptions,
    roleOptions,
    sectorOptions,
    unmatchedAxisValues,
  ]);

  // "Showing X only." / the Active-filters "Saved as" name — every active
  // filter's label, composite links joined with " + ". Includes each axis's
  // still-unmatched raw values too, so the proposed name matches what
  // buildFilterSearchString actually saves (same reasoning as there).
  const activeOnboardingLabel = useMemo(() => {
    const labelsFor = (axis, values, options) => {
      const resolved = values
        .filter((v) => v !== NO_TOOL_VALUE)
        .map((v) => options.find((opt) => opt.value === v)?.label)
        .filter(Boolean);
      const unmatched = unmatchedAxisValues.filter((u) => u.axis === axis).map((u) => u.label);
      return [...resolved, ...unmatched].join(", ");
    };
    return [
      labelsFor("tool", selectedTools, toolOptions),
      labelsFor("category", selectedCategories, categoryOptions),
      labelsFor("role", selectedRoles, roleOptions),
      labelsFor("sector", selectedSectors, sectorOptions),
    ]
      .filter(Boolean)
      .join(" + ");
  }, [
    selectedTools,
    selectedCategories,
    selectedRoles,
    selectedSectors,
    toolOptions,
    categoryOptions,
    roleOptions,
    sectorOptions,
    unmatchedAxisValues,
  ]);

  // Recomputed from live filter state (not the URL) so "Saved as" disappears
  // the moment a dropdown tick no longer matches the saved combination.
  const shortcutMatch = useMemo(
    () => findByFilters(buildFilterSearchString()),
    [findByFilters, buildFilterSearchString],
  );

  // Shared by both entry points — the floating first-load prompt's "Save
  // shortcut" button (no name, defaults to the auto-derived label) and the
  // Task Library's inline "+ Save as Shortcut" field (a name the user typed
  // or left as that same default). Either way, saving always surfaces the
  // "saved / Show me" confirmation popup.
  const handleSaveShortcut = useCallback(async (name) => {
    const created = await saveShortcut(name || activeOnboardingLabel || "Shortcut", buildFilterSearchString());
    setSavedShortcutId(created?.id || null);
    setPopupPhase("saved");
  }, [saveShortcut, activeOnboardingLabel, buildFilterSearchString]);

  const handleShowShortcut = useCallback(() => {
    if (savedShortcutId) requestShortcutHighlight(savedShortcutId);
    setPopupPhase("hidden");
  }, [savedShortcutId]);

  const scopedList = scopeBaseList;

  const filteredTasks = useMemo(() => {
    const list = scopedList;
    const term = search.trim().toLowerCase();
    const wantsNoTool = selectedTools.includes(NO_TOOL_VALUE);
    // Normalised the same way as toolMatchCounts/connectedToolKeys, since a
    // selected tool's value can come from either a task's integrations entry
    // or an integration's provider key, which spell the same tool
    // differently ("Google Docs" vs "google-docs").
    const toolSelections = selectedTools
      .filter((s) => s !== NO_TOOL_VALUE)
      .map((s) => normaliseToolKey(s));
    return list.filter((t) => {
      if (selectedTools.length > 0) {
        const normalisedNames = getTaskToolNames(t).map((name) => normaliseToolKey(name));
        const matchesNoTool = wantsNoTool && normalisedNames.length === 0;
        const matchesTool =
          toolSelections.length > 0 &&
          toolSelections.some((sel) => normalisedNames.includes(sel));
        if (!matchesNoTool && !matchesTool) return false;
      }
      if (selectedCategories.length > 0 && !selectedCategories.includes(t?.category)) return false;
      if (
        selectedRoles.length > 0 &&
        !(t?.job_roles || []).some((id) => selectedRoles.includes(id))
      )
        return false;
      if (
        selectedSectors.length > 0 &&
        !(t?.sectors || []).some((id) => selectedSectors.includes(id))
      )
        return false;
      if (!term) return true;
      return (
        (t?.title || "").toLowerCase().includes(term) ||
        (t?.prompt_value || "").toLowerCase().includes(term)
      );
    });
  }, [scopedList, search, selectedTools, selectedCategories, selectedRoles, selectedSectors]);

  const isLibraryLoading =
    scope === "proven"
      ? !provenTasksLoaded
      : scope === "team" || scope === "scheduled"
        ? !teamTasksLoaded
        : !yourTasksLoaded;

  // Real names for the Team tab + the active-scope chip. locationUsers is the
  // same source ShareModal uses for "share with a teammate".
  const teamRoster = useMemo(() => {
    const meId = String(userInfo?._id || "");
    const mapped = (locationUsers || []).map((u) => ({
      id: String(u?._id),
      name: u?.userName || "Teammate",
      isCurrentUser: String(u?._id) === meId,
    }));
    if (meId && !mapped.some((m) => m.isCurrentUser)) {
      mapped.unshift({ id: meId, name: userInfo?.userName || "You", isCurrentUser: true });
    }
    return mapped.sort((a, b) => (a.isCurrentUser === b.isCurrentUser ? 0 : a.isCurrentUser ? -1 : 1));
  }, [locationUsers, userInfo]);

  const toggleTeamMember = useCallback((memberId, isCurrentUser) => {
    if (isCurrentUser) {
      setScope("you");
      setSelectedTeamMembers([]);
      return;
    }
    setScope("team");
    setSelectedTeamMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  }, []);

  const handleScopeChange = useCallback((nextScope) => {
    setScope(nextScope);
    if (nextScope !== "team") setSelectedTeamMembers([]);
  }, []);

  const resetScope = useCallback(() => {
    setScope("you");
    setSelectedTeamMembers([]);
  }, []);

  const clearAllFilters = useCallback(() => {
    resetScope();
    clearTools();
    setSelectedCategories([]);
    setSelectedRoles([]);
    setSelectedSectors([]);
    setUnmatchedAxisValues([]);
  }, [resetScope, clearTools]);

  const dismissUnmatchedAxisValue = useCallback((axis, label) => {
    setUnmatchedAxisValues((prev) => prev.filter((u) => !(u.axis === axis && u.label === label)));
  }, []);

  const scopeChipLabel = useMemo(() => {
    if (scope === "scheduled") return "Scheduled";
    if (scope === "proven") return "Proven";
    if (scope === "team") {
      if (selectedTeamMembers.length === 0) return "Whole team";
      if (selectedTeamMembers.length === 1) {
        return teamRoster.find((m) => m.id === selectedTeamMembers[0])?.name || "Whole team";
      }
      return `${selectedTeamMembers.length} teammates`;
    }
    return null;
  }, [scope, selectedTeamMembers, teamRoster]);

  const handleTaskCardClick = useCallback((task) => {
    const taskPrompt = task?.prompt_value?.trim();
    if (!taskPrompt) {
      toast.error("This task does not have prompt content to run.");
      return;
    }
    setPrefillMessage(taskPrompt);
  }, []);

  const submitNewMessage = async (
    prompt,
    type = "savetodb",
    messagetype = "notimage",
    setNewMessage,
    addInMessages = true,
  ) => {
    setIsUserChating(true)
    if (selectedFiles.length > 10) {
      const container = document.getElementById("errorofmaxfile");
      if (container) {
        const div = document.createElement("div");
        div.style.display = "flex";
        div.style.justifyContent = "center";
        div.style.position = "relative";
        div.className = "file_upload_input";
        div.innerHTML = `
        <span style="position: absolute; top: 5px; right: 10px; cursor: pointer;" onclick="this.parentElement.remove()">&#10005;</span>
        <p style="padding: 15px; margin: 0px; color: red; text-align: center;">
            You can only upload 10 files at a time
        </p>
      `;
        container.appendChild(div);
        setTimeout(() => {
          div.remove();
        }, 5000);
      }
      return true;
    }

    let resolvedFiles = selectedFiles;
    const pendingFiles = selectedFiles.filter((f) => f instanceof File);
    if (pendingFiles.length > 0) {
      try {
        setIsUserChating(true)
        const response = await contextuploadFilefrominput(
          pendingFiles,
          params?.id,
          () => { },
        );
        console.log("upload response:", response?.data);
        if (response?.data?.success === true && response?.data?.data) {
          const uploaded = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          let idx = 0;
          resolvedFiles = selectedFiles
            .map((f) => (f instanceof File ? uploaded[idx++] : f))
            .filter(Boolean);
        } else {
          resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
        }
      } catch (err) {
        console.error("file upload failed:", err);
        resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
      }
    }

    const fileToUpload = JSON.parse(JSON.stringify(resolvedFiles));
    const effectivePrompt =
      prompt && prompt.trim()
        ? prompt
        : fileToUpload?.length > 0
          ? ""
          : prompt;
    setSelectedFiles([]);
    if (!params.id) {
      const payload = {
        fileData: fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })),
        message: effectivePrompt,
        messagetype,
        addInMessages,
      };
      createConvesation(payload);
      setNewMessage && setNewMessage("");
      return;
    }

    const newMsgObject = {
      content: effectivePrompt,
      sender: null,
      time: new Date().toLocaleTimeString(),
      status: "delivered",
      file_type: messagetype,
      assistant_name: selectedAssistant?.assistant_name || "",
      file_ids:
        fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })) || [],
    };

    const messagePayload = {
      conversation_id: params?.id,
      type: type,
      message: newMsgObject,
    };
    handleSendMessage(messagePayload);
    setNewMessage("");
    if (addInMessages) {
      setMessages((prev) => [...prev, newMsgObject]);
      markMessagePending(newMsgObject);
    }
    document.getElementById("gotovie")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      // In a conversation the bottom padding moves off the scroll panel so the
      // sticky composer sits flush with the panel edge (no see-through strip).
      className={`h-[calc(100vh-85px)] overflow-y-auto max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 ${params.id ? "pt-8" : "pt-14 md:pt-20 pb-16"} [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]`}
    >
      {!params.id ? (
        <div className="max-w-3xl mx-auto w-full mb-8 text-left">
          <div className="page-eyebrow mb-3">Tasks</div>
          <h1 className="page-display text-3xl md:text-4xl">
            <span className="capitalize">Hi {userInfo?.userName}.</span>{" "}
            <span className="accent">Start with one Task.</span>
          </h1>

          {isUserChating && (
            <div className="relative mt-5">
              {selectedFiles?.length > 0 ? (
                <div className="flex items-center gap-1 text-muted-foreground text-[14px]">
                  <Icon
                    id="aichatIconInput"
                    style={{ marginRight: "5px", fontSize: "12px" }}
                  />
                  Analysing files
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground text-[14px]">
                  <CircularProgress size={16} />
                  Starting your task...
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Conversation
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
        />
      )}

      <div
        // In a conversation the composer pins to the bottom of the scroll
        // panel so it never drops off-screen as messages grow (CU-86carbt97).
        className={`max-w-3xl mx-auto w-full ${params.id ? "sticky bottom-0 z-10 bg-background" : ""
          }`}
      >
        {!params.id && <div id="errorofmaxfile" />}
        {taskIdUnavailable && (
          <p className="text-xs text-muted-foreground mb-2 px-2">
            This Task is no longer available.
          </p>
        )}
        <ChatInput
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
          prefillMessage={prefillMessage}
          onPrefillConsumed={handlePrefillConsumed}
          toolGhostLabel={toolGhostLabel}
        />

        <div className="flex items-center justify-end mt-5 pb-6 px-2">
          <span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
            <span aria-hidden className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-[hsl(var(--task-accent))] opacity-60 animate-ping" />
              <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--task-accent))]" />
            </span>
            Best AI picked for every Task
          </span>
        </div>
      </div>

      {selectedAssistant?.assistant_name !== 'Agent Builder' && <div id="task-library" className="mt-8 scroll-mt-6">
        <TasksFilterBar
          scope={scope}
          onScopeChange={handleScopeChange}
          scopeChipLabel={scopeChipLabel}
          onResetScope={resetScope}
          counts={{
            you: yourPool.length,
            team: wholeTeamPool.length,
            scheduled: scheduledPool.length,
            proven: provenPool.length,
          }}
          count={filteredTasks.length}
          inlineHint={toolGhostLabel ? `Select a Task to run with ${toolGhostLabel}` : null}
          selectedTools={selectedTools}
          onToggleTool={toggleTool}
          toolOptions={toolOptions}
          noToolCount={noToolCount}
          teamMembers={teamRoster}
          selectedTeamMembers={selectedTeamMembers}
          onToggleTeamMember={toggleTeamMember}
          onClearAll={clearAllFilters}
          unmatchedAxisValues={unmatchedAxisValues}
          onDismissUnmatchedAxisValue={dismissUnmatchedAxisValue}
          categoryOptions={categoryOptions}
          selectedCategories={selectedCategories}
          onToggleCategory={toggleCategory}
          roleOptions={roleOptions}
          selectedRoles={selectedRoles}
          onToggleRole={toggleRole}
          sectorOptions={sectorOptions}
          selectedSectors={selectedSectors}
          onToggleSector={toggleSector}
          onSaveShortcut={activeOnboardingLabel ? handleSaveShortcut : undefined}
          defaultShortcutName={activeOnboardingLabel}
          shortcutMatch={shortcutMatch}
          onRenameShortcut={renameShortcut}
          onRemoveShortcut={removeShortcut}
        />

        {isLibraryLoading && filteredTasks.length === 0 ? (
          // Skeleton grid on FIRST load only, per tab — same shape as
          // YourAIAgents.jsx's agent skeleton, sized to TaskCard instead.
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="rounded-xl p-5 bg-card border border-border h-full flex flex-col animate-pulse"
              >
                <div className="h-5 w-3/4 rounded bg-muted mb-2" />
                <div className="h-3.5 w-full rounded bg-muted mb-1.5" />
                <div className="h-3.5 w-5/6 rounded bg-muted mb-5" />
                <div className="mt-auto flex flex-col gap-3">
                  <div className="h-3.5 w-24 rounded bg-muted" />
                  <div className="h-9 w-full rounded-md bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(libraryExpanded ? filteredTasks : filteredTasks.slice(0, 5)).map((task) => {
              const isChatUi =
                !task.output || task.output.toLowerCase() === "chatui";
              const tools = isChatUi ? [] : [task.output];
              return (
                <TaskCard
                  key={task._id || task.id}
                  title={task.title}
                  description={task.prompt_value}
                  metaLabel={
                    task.repeat && task.repeat !== "none"
                      ? `Repeats ${task.repeat}`
                      : "Run anytime"
                  }
                  tools={tools}
                  buttonLabel="Run now"
                  scheduleLabel={
                    isScheduled(task) ? formatCadence(task.repeat) : undefined
                  }
                  suggestedLabel={
                    isScheduled(task)
                      ? undefined
                      : suggestAutomation({
                        title: task.title,
                        description: task.prompt_value,
                      })
                  }
                  onClick={() => handleTaskCardClick(task)}
                  {...bindActions(task)}
                />
              );
            })}

            {!libraryExpanded && filteredTasks.length - 5 > 0 && (
              <TaskCard
                title={`Explore ${filteredTasks.length - 5}+ tasks`}
                description="Sales, ops, HR, finance and more — or write your own from scratch."
                metaLabel="Ready-made templates"
                buttonLabel="Browse tasks"
                showActions={false}
                onClick={() => navigate("#task-library")}
              />
            )}
          </div>
        )}

        {scope !== "proven" && (
          <button
            type="button"
            onClick={() => {
              setScope("proven");
              setSelectedTeamMembers([]);
              setSearch("");
              clearTools();
            }}
            className="interactive-surface mt-4 w-full rounded-xl border border-dashed border-border/70 bg-transparent px-5 py-4 flex items-center justify-between text-left group"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                Browse Proven Tasks Templates
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                The collective playbook. Battle-tested by teams across a range
                of sectors, ready for you to run in seconds.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground group-hover:text-foreground transition-colors shrink-0 ml-4">
              Proven Tasks
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        )}

        {libraryExpanded && filteredTasks.length > 5 && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() =>
                navigate(
                  { pathname: location.pathname, search: location.search, hash: "" },
                  { replace: true }
                )
              }
              className="text-[13px] text-muted-foreground underline hover:text-foreground"
            >
              Show fewer
            </button>
          </div>
        )}

        {!isLibraryLoading && filteredTasks.length === 0 && (
          <div className="text-center text-[13px] text-muted-foreground py-12">
            No tasks match your filters.
          </div>
        )}
      </div>}

      {!params.id && (
        <footer className="mt-12 border-t border-border/40 py-4">
          <p className="text-xs text-muted-foreground/60 text-center">
            Your data isn't used to train models. AI can make mistakes, so
            review before you ship.
          </p>
        </footer>
      )}

      {actionModals}

      {(popupPhase === "saved" || (popupPhase === "prompt" && !shortcutMatch)) && (
        <SaveShortcutPopup
          label={activeOnboardingLabel || "these Tasks"}
          saved={popupPhase === "saved"}
          onSave={() => handleSaveShortcut()}
          onDismiss={() => setPopupPhase("hidden")}
          onShowMe={handleShowShortcut}
        />
      )}
    </div>
  );
};

export default ConversationWrapper;
